"use client";

import React from "react";
import Image from "next/image";
import EvolutionArrow from "~/components/shared/EvolutionArrow";
import { type EvolutionsRecord, type EvolutionData } from "~/types/pokemon";
import { getPokemonImageSrc, getPokemonImageAlt } from "~/lib/pokemon";
import {
  BsSunFill,
  BsMoonFill,
  BsPersonFill,
  BsBoxSeamFill,
} from "react-icons/bs";

import { useTranslations, useLocale } from "next-intl";
import { Link } from "~/i18n/navigation";
import { getPokemonUrl } from "~/lib/routing";

const EVOLUTION_ITEM_TO_KEY: Record<string, string> = {
  "Sun Stone": "item_1101",
  "King's Rock": "item_1102",
  "Kings Rock": "item_1102",
  "Metal Coat": "item_1103",
  "Dragon Scale": "item_1104",
  "Upgrade": "item_1105",
  "Up-Grade": "item_1105",
  "Sinnoh Stone": "item_1106",
  "Unova Stone": "item_1107",
  "Gimmighoul Coins": "item_1150",
  "Gimmighoul Coin": "item_1150",
  "Sweet Apple": "item_1151",
  "Tart Apple": "item_1152",
  "Syrupy Apple": "item_1155",
  "Zygarde Cell": "item_650",
};

const EVOLUTION_ITEM_FALLBACKS: Record<string, Record<string, string>> = {
  de: {
    "Sun Stone": "Sonnenstein", "King's Rock": "King-Stein", "Kings Rock": "King-Stein",
    "Metal Coat": "Metallmantel", "Dragon Scale": "Drachenhaut", "Upgrade": "Up-Grade", "Up-Grade": "Up-Grade",
    "Sinnoh Stone": "Sinnoh-Stein", "Unova Stone": "Einall-Stein", "Gimmighoul Coins": "Gierspenst-Münzen",
    "Sweet Apple": "Süßer Apfel", "Tart Apple": "Saurer Apfel", "Syrupy Apple": "Wirr-Apfel", "Zygarde Cell": "Zygarde-Zelle",
  },
  es: {
    "Sun Stone": "Piedra Solar", "King's Rock": "Roca del Rey", "Kings Rock": "Roca del Rey",
    "Metal Coat": "Revestimiento Metálico", "Dragon Scale": "Escama Dragón", "Upgrade": "Mejora", "Up-Grade": "Mejora",
    "Sinnoh Stone": "Piedra Sinnoh", "Unova Stone": "Piedra Teselia", "Gimmighoul Coins": "Monedas de Gimmighoul",
    "Sweet Apple": "Manzana Dulce", "Tart Apple": "Manzana Ácida", "Syrupy Apple": "Manzana Confitada", "Zygarde Cell": "Célula de Zygarde",
  },
  fr: {
    "Sun Stone": "Pierre Soleil", "King's Rock": "Roche Royale", "Kings Rock": "Roche Royale",
    "Metal Coat": "Peau Métal", "Dragon Scale": "Écaille Draco", "Upgrade": "Améliorator", "Up-Grade": "Améliorator",
    "Sinnoh Stone": "Pierre Sinnoh", "Unova Stone": "Pierre Unys", "Gimmighoul Coins": "Pièces de Mordudor",
    "Sweet Apple": "Pomme Sucrée", "Tart Apple": "Pomme Acidulée", "Syrupy Apple": "Pomme Nectar", "Zygarde Cell": "Cellule de Zygarde",
  },
  it: {
    "Sun Stone": "Pietrasolare", "King's Rock": "Roccia di Re", "Kings Rock": "Roccia di Re",
    "Metal Coat": "Metalcoperta", "Dragon Scale": "Squama Drago", "Upgrade": "Upgrade", "Up-Grade": "Upgrade",
    "Sinnoh Stone": "Pietra Sinnoh", "Unova Stone": "Pietra Unima", "Gimmighoul Coins": "Monete di Gimmighoul",
    "Sweet Apple": "Dolcepomo", "Tart Apple": "Aspropomo", "Syrupy Apple": "Sciroppomo", "Zygarde Cell": "Cellula di Zygarde",
  },
  "pt-br": {
    "Sun Stone": "Pedra Solar", "King's Rock": "Pedra do Rei", "Kings Rock": "Pedra do Rei",
    "Metal Coat": "Revestimiento Metálico", "Dragon Scale": "Escama de Dragão", "Upgrade": "Melhora", "Up-Grade": "Melhora",
    "Sinnoh Stone": "Pedra de Sinnoh", "Unova Stone": "Pedra de Unova", "Gimmighoul Coins": "Moedas de Gimmighoul",
    "Sweet Apple": "Maçã Doce", "Tart Apple": "Maçã Ácida", "Syrupy Apple": "Maçã do Amor", "Zygarde Cell": "Célula de Zygarde",
  },
  ja: {
    "Sun Stone": "たいようのいし", "King's Rock": "おうじゃのしるし", "Kings Rock": "おうじゃのしるし",
    "Metal Coat": "メタルコート", "Dragon Scale": "りゅうのウロコ", "Upgrade": "アップグレード", "Up-Grade": "アップグレード",
    "Sinnoh Stone": "シンオウのいし", "Unova Stone": "イッシュのいし", "Gimmighoul Coins": "コレクレーのコイン",
    "Sweet Apple": "あまーいりんご", "Tart Apple": "すっぱーいりんご", "Syrupy Apple": "みつあめりんご", "Zygarde Cell": "ジガルデ・セル",
  },
  ko: {
    "Sun Stone": "태양의돌", "King's Rock": "왕의징표석", "Kings Rock": "왕의징표석",
    "Metal Coat": "금속코트", "Dragon Scale": "용의비늘", "Upgrade": "업그레이드", "Up-Grade": "업그레이드",
    "Sinnoh Stone": "신오의 돌", "Unova Stone": "하나의 돌", "Gimmighoul Coins": "모으령의 코인",
    "Sweet Apple": "달콤한사과", "Tart Apple": "새콤한사과", "Syrupy Apple": "꿀사과", "Zygarde Cell": "지가르데·셀",
  },
  "zh-tw": {
    "Sun Stone": "日之石", "King's Rock": "王者之證", "Kings Rock": "王者之證",
    "Metal Coat": "金屬膜", "Dragon Scale": "龍之鱗片", "Upgrade": "升級資料", "Up-Grade": "升級資料",
    "Sinnoh Stone": "神奧之石", "Unova Stone": "合眾之石", "Gimmighoul Coins": "索財靈的硬幣",
    "Sweet Apple": "甜甜蘋果", "Tart Apple": "酸酸蘋果", "Syrupy Apple": "蜜汁蘋果", "Zygarde Cell": "基格爾德·細胞",
  },
};

interface EvolutionBoxProps {
  evolutions: EvolutionsRecord;
}

const EvolutionBox = ({ evolutions }: EvolutionBoxProps) => {
  const locale = useLocale();
  const t = useTranslations("Pokemon");
  const gamemasterT = useTranslations("Gamemaster");

  const safeT = (key: string, fallback?: string) => {
    try {
      return gamemasterT.has(key) ? gamemasterT(key) : (fallback ?? key);
    } catch {
      return fallback ?? key;
    }
  };

  const getLocalizedEvolutionItem = (itemReq: string) => {
    const key = EVOLUTION_ITEM_TO_KEY[itemReq];
    if (key && gamemasterT.has(key)) {
      return gamemasterT(key);
    }
    return EVOLUTION_ITEM_FALLBACKS[locale]?.[itemReq] ?? itemReq;
  };

  const getLocalizedQuestRequirement = (
    req: EvolutionData["questRequirement"],
  ) => {
    if (!req) return "";
    const assetsRef = req.assetsRef;
    if (assetsRef) {
      const candidates = [assetsRef, `quest_title_${assetsRef}`];
      for (const k of candidates) {
        if (gamemasterT.has(k)) {
          let str = gamemasterT(k);
          const target = req.target !== undefined ? String(req.target) : "";
          str = str.replace(/\{\{amount(?:_0)?\}\}/g, target);
          str = str.replace(/\{amount\}/g, target);
          return str;
        }
      }
    }
    return req.translated ?? req.i18n ?? "";
  };

  // If the object is only 1 long, don't display evolutions at all.
  if (Object.keys(evolutions).length <= 1) return null;

  const megaEvolutions = Object.values(evolutions).filter(
    (e) => e.tempEvoId && !e.unreleased,
  );
  const regularEvolutions = evolutions;

  // Path Generation Logic
  const keys = Object.keys(regularEvolutions).filter(
    (k) =>
      !regularEvolutions[k]?.tempEvoId && !regularEvolutions[k]?.unreleased,
  );
  const leafKeys = keys.filter(
    (k) => !keys.some((otherK) => regularEvolutions[otherK]?.evolvesFrom === k),
  );

  const paths: EvolutionData[][] = leafKeys.map((leafKey) => {
    const path: EvolutionData[] = [];
    let currentKey: string | null | undefined = leafKey;
    while (currentKey && regularEvolutions[currentKey]) {
      const node: EvolutionData = regularEvolutions[currentKey]!;
      path.unshift(node);
      currentKey = node.evolvesFrom;
    }
    return path;
  });

  const PokemonNode = ({ evo }: { evo: EvolutionData }) => (
    <Link
      href={getPokemonUrl(evo.pokemon, evo.form ?? 0, 0)}
      className="flex flex-col items-center hover:opacity-80 transition-opacity"
    >
      <div className="relative mb-2 flex h-20 w-20 items-center justify-center rounded-2xl border border-neutral-100 shadow-inner sm:h-24 sm:w-24">
        <Image
          src={getPokemonImageSrc(evo.image_url, false, evo.shiny_image_url)}
          className="h-16 w-16 object-contain drop-shadow-md sm:h-20 sm:w-20"
          alt={getPokemonImageAlt(
            safeT(`poke_${evo.pokemon}`),
            evo.image_url,
            false,
            evo.form && evo.form !== 0 ? safeT(`form_${evo.form}`) : undefined,
            undefined,
            locale,
          )}
          width={80}
          height={80}
        />
      </div>
      <span className="text-pory-blue-900 text-center text-xs leading-tight font-bold sm:text-sm">
        {safeT(`poke_${evo.pokemon}`)}
        {evo.form !== 0 &&
          gamemasterT.has(`form_${evo.form}`) &&
          ` (${safeT(`form_${evo.form}`)})`}
      </span>
      <span className="text-pory-blue-700 text-[10px] font-semibold">
        #{evo.pokemon.toString().padStart(4, "0")}
      </span>
    </Link>
  );

  const RequirementInfo = ({ evo }: { evo: EvolutionData }) => (
    <div className="flex flex-col items-center gap-1">
      <div className="flex flex-wrap justify-center gap-1">
        {evo.mustBeBuddy && (
          <div className="text-pory-blue-600 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold">
            <BsPersonFill size={10} />
            {t("buddy")}
          </div>
        )}
        {evo.onlyDaytime && (
          <div className="flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-bold text-yellow-800">
            <BsSunFill size={10} />
            {t("day")}
          </div>
        )}
        {evo.onlyNighttime && (
          <div className="flex items-center gap-1 rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-bold text-white">
            <BsMoonFill size={10} />
            {t("night")}
          </div>
        )}
        {evo.itemRequirement && (
          <div className="flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-600">
            <BsBoxSeamFill size={10} />
            {getLocalizedEvolutionItem(evo.itemRequirement)}
          </div>
        )}
        {evo.genderRequirement && (
          <div className="flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600">
            <span className="text-xs">
              {safeT(`gender_icon_${evo.genderRequirement}`)}
            </span>
            {safeT(`gender_${evo.genderRequirement}`)}
          </div>
        )}
      </div>
      {evo.questRequirement && (
        <span className="bg-pory-pink-50 text-pory-pink-600 border-pory-pink-100 max-w-[150px] rounded-lg border px-2 py-1 text-center text-[10px] leading-tight font-semibold italic">
          {getLocalizedQuestRequirement(evo.questRequirement)}
        </span>
      )}
    </div>
  );

  return (
    <section
      id="evolution"
      className="flex flex-col gap-6 rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm"
    >
      <h2 className="text-pory-blue-900 text-xl font-bold">{t("evolution")}</h2>

      {/* Regular Evolution paths */}
      <div className="flex flex-col gap-10">
        {paths.map((path, pIdx) => (
          <div
            key={`path-${pIdx}`}
            className="flex flex-wrap items-center justify-center gap-4 sm:gap-8"
          >
            {path.map((node, nIdx) => (
              <React.Fragment key={`${node.pokemon}_${node.form}`}>
                {nIdx > 0 && (
                  <div className="flex flex-col items-center gap-2">
                    <EvolutionArrow cost={node.candyCost ?? 0} />
                    <RequirementInfo evo={node} />
                  </div>
                )}
                <PokemonNode evo={node} />
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>

      {/* Mega Evolutions */}
      {megaEvolutions.length > 0 && (
        <div className="mt-8 flex flex-col gap-6 border-t border-neutral-100 pt-8">
          <h3 className="text-pory-blue-800 text-center font-bold tracking-wide uppercase">
            {t("megaEvolutions")}
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {megaEvolutions.map((mega) => {
              const megaName = gamemasterT.has(`poke_${mega.pokemon}_e${mega.tempEvoId}`)
                ? safeT(`poke_${mega.pokemon}_e${mega.tempEvoId}`)
                : `${mega.tempEvoId === 4 ? t("primal") : t("mega")} ${safeT(`poke_${mega.pokemon}`)}`;

              return (
              <div
                key={`${mega.pokemon}_${mega.form}_${mega.tempEvoId}`}
                className="border-pory-blue-100 flex flex-col gap-4 rounded-xl border bg-white/50 p-4 shadow-sm backdrop-blur-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-neutral-100 shadow-inner">
                    <Image
                      src={getPokemonImageSrc(
                        mega.image_url,
                        false,
                        mega.shiny_image_url,
                      )}
                      className="h-16 w-16 object-contain drop-shadow-sm"
                      alt={getPokemonImageAlt(megaName, mega.image_url, false, undefined, undefined, locale)}
                      width={64}
                      height={64}
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-pory-blue-900 font-bold">
                      {megaName}
                    </span>
                    <div className="flex gap-2">
                      <span className="bg-pory-pink-100 text-pory-pink-600 rounded-full px-2 py-0.5 text-[10px] font-bold">
                        {t("energyCost", {
                          first: mega.firstEnergyCost ?? 0,
                          subsequent: mega.subsequentEnergyCost ?? 0,
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                {mega.stats && (
                  <div className="grid grid-cols-3 gap-2 border-t border-neutral-100 pt-3">
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] font-bold text-neutral-500 uppercase">
                        {t("attack")}
                      </span>
                      <span className="text-pory-blue-900 font-bold">
                        {mega.stats.baseAttack}
                      </span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] font-bold text-neutral-500 uppercase">
                        {t("defence")}
                      </span>
                      <span className="text-pory-blue-900 font-bold">
                        {mega.stats.baseDefense}
                      </span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] font-bold text-neutral-500 uppercase">
                        {t("stamina")}
                      </span>
                      <span className="text-pory-blue-900 font-bold">
                        {mega.stats.baseStamina}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default EvolutionBox;
